import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, fecha, num, toDateValue } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_DATE, FMT_NUM, fileNameConFecha } from '../lib/xlsx';
import type { Insumo, Movimiento } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';

type ListaCompras = { items: Movimiento[]; total: number; totalMonto: number };

export function ComprasPage() {
  const { tiene } = useAuth();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [insumoId, setInsumoId] = useState('');

  const insumosQ = useQuery({
    queryKey: ['insumos'],
    queryFn: () => API.get<Insumo[]>('/insumos'),
    enabled: tiene('insumos:read'),
  });

  const q = useQuery({
    queryKey: ['compras', desde, hasta, insumoId],
    queryFn: () => {
      const p = new URLSearchParams();
      if (desde) p.set('desde', desde);
      if (hasta) p.set('hasta', hasta);
      if (insumoId) p.set('insumoId', insumoId);
      p.set('limit', '300');
      return API.get<ListaCompras>(`/compras?${p.toString()}`);
    },
    enabled: tiene('compras:read'),
  });

  function exportarXlsx() {
    const items = q.data?.items ?? [];
    if (items.length === 0) return;
    downloadXlsx(fileNameConFecha('compras'), [
      {
        name: 'Compras',
        rows: items,
        columns: [
          { header: 'Fecha', value: (m) => toDateValue(m.fecha), width: 12, numFmt: FMT_DATE },
          { header: 'Insumo', value: (m) => m.insumo?.nombre ?? '', width: 20 },
          { header: 'Categoría', value: (m) => m.categoria?.nombre ?? '', width: 20 },
          { header: 'Subcategoría', value: (m) => m.subcategoria?.nombre ?? '', width: 20 },
          { header: 'Descripción', value: (m) => m.descripcion ?? '', width: 30 },
          { header: 'Cantidad', value: (m) => (m.cantidad ? Number(m.cantidad) : null), width: 10, numFmt: FMT_NUM },
          { header: 'Unidad', value: (m) => m.unidad ?? m.insumo?.unidad ?? '', width: 8 },
          {
            header: 'Precio unitario',
            value: (m) => {
              const cant = m.cantidad ? Number(m.cantidad) : 0;
              return cant > 0 ? Number(m.monto) / cant : null;
            },
            width: 14,
            numFmt: FMT_COP,
          },
          { header: 'Total', value: (m) => Number(m.monto), width: 14, numFmt: FMT_COP },
          { header: 'Método de pago', value: (m) => m.metodoPago ?? '', width: 14 },
        ],
      },
    ]);
  }

  const activeFilters = (desde ? 1 : 0) + (hasta ? 1 : 0) + (insumoId ? 1 : 0);

  const columns: Column<Movimiento>[] = [
    {
      key: 'fechaInsumo',
      header: 'Fecha',
      mobile: 'primary',
      render: (m) => (
        <div>
          <div className="text-sm font-semibold">{m.insumo?.nombre ?? m.descripcion ?? 'Compra'}</div>
          <div className="text-xs text-slate-500">{fecha(m.fecha)}</div>
        </div>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      mobile: 'secondary',
      align: 'right',
      render: (m) => <span className="font-semibold text-red-600">{cop(Number(m.monto))}</span>,
    },
    { key: 'cat', header: 'Categoría', render: (m) => m.categoria?.nombre ?? '—' },
    { key: 'desc', header: 'Descripción', render: (m) => m.descripcion ?? '—' },
    {
      key: 'cant',
      header: 'Cantidad',
      align: 'right',
      render: (m) => {
        const cant = m.cantidad ? Number(m.cantidad) : 0;
        return cant > 0 ? `${num(cant, 2)} ${m.unidad ?? m.insumo?.unidad ?? ''}` : '—';
      },
    },
    {
      key: 'unit',
      header: 'Precio unit.',
      align: 'right',
      render: (m) => {
        const cant = m.cantidad ? Number(m.cantidad) : 0;
        const unit = cant > 0 ? Number(m.monto) / cant : null;
        return unit ? cop(unit) : '—';
      },
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Compras"
        subtitle="Todo lo que has comprado: materia prima, insumos y otros gastos."
      />

      <div className="card p-3 sm:p-4 space-y-3">
        <FilterBar activeCount={activeFilters}>
          <div>
            <label className="label">Desde</label>
            <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <label className="label">Hasta</label>
            <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          <div>
            <label className="label">Insumo</label>
            <select className="input" value={insumoId} onChange={(e) => setInsumoId(e.target.value)}>
              <option value="">Todos</option>
              {insumosQ.data?.map((i) => (
                <option key={i.id} value={i.id}>{i.nombre}</option>
              ))}
            </select>
          </div>
        </FilterBar>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-600">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span><b>{q.data?.total ?? 0}</b> compras</span>
            <span className="font-semibold text-red-600">{cop(q.data?.totalMonto)} en total</span>
          </div>
          <button
            type="button"
            className="btn-secondary w-full sm:w-auto"
            onClick={exportarXlsx}
            disabled={!q.data?.items?.length}
            title="Descargar como .xlsx"
          >
            ⬇ Exportar .xlsx
          </button>
        </div>
      </div>

      <div className="card">
        <ResponsiveTable<Movimiento>
          columns={columns}
          rows={q.data?.items}
          rowKey={(m) => m.id}
          loading={q.isLoading}
          empty="Sin compras."
        />
      </div>
    </div>
  );
}
